import React, { useEffect, useMemo, useState } from "react";
import { Popover, Typography, Box } from "@mui/material";
import { ChevronRight, Close } from "@mui/icons-material";

import { useTranslation } from "react-i18next";
import { FilterIconSvg } from "../../../Features/Table/Icons";
import {
  CancelFormActionButton,
  ActiveButtonBadge,
  FilterBoxContainer,
  FilterBoxTooltip,
  PrimaryFormActionButton,
  PrimaryIconButton,
  PrimaryTextButton,
} from "../../../Features/Table/GenericDraggableTable/TableStyles";
import { changeDateFormat } from "../../../Helper/DatePicker/DateConverter";
import RecordCountBox from "../../../Components/RecordCountBox";
import FilterBody from "../../../Features/Table/GenericDraggableTable/TableFilters/FilterBody";
import { QuickFilterBox } from "./StyledComponents";

const styles = {
  popoverPaper: {
    width: 554,
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    boxShadow:
      "-2px -2px 4px 0px rgba(151, 151, 151, 0.25), 2px 2px 4px 4px rgba(151, 151, 151, 0.25)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid #E0E0E0",
    padding: "12px 16px 12px 12px",
    justifyContent: "space-between",
  },
  headerText: {
    fontFamily: "Roboto",
    fontWeight: 700,
    fontSize: "14px",
    lineHeight: "24px",
    color: "#3D4B5A",
    px: 1,
  },
  footer: {
    p: 1,
    borderTop: "1px solid #E0E0E0",
    display: "flex",
    justifyContent: "flex-end",
  },
};

const FilterBox = ({
  label,
  valuesText,
  onClick,
  onRemove,
  isActive,
  tooltipTitle = "No title",
}) => {
  const [isHovered, setIsHovered] = useState(false);
  let hasActive = isActive || isHovered;

  return (
    <FilterBoxTooltip title={tooltipTitle} placement={"bottom"}>
      <FilterBoxContainer
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        hasActive={hasActive}
      >
        {label && (
          <Typography
            variant={hasActive ? "fw500s12lh246D72F6" : "fw500s12lh24c808080"}
          >
            {label}:
          </Typography>
        )}

        <Typography
          variant={hasActive ? "fw500s12lh24c4D44D8" : "fw500s12lh24c474747"}
        >
          {valuesText}
        </Typography>

        {onRemove && <Close onClick={onRemove} />}
      </FilterBoxContainer>
    </FilterBoxTooltip>
  );
};

export default function MapDigitalTwinFilters({
  filters = {},
  selectedFilters = {},
  setSelectedFilters = () => {},
  container
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation(["common", "generic_table"]);
  const open = Boolean(anchorEl);
  const id = open ? "filter-popover" : undefined;
  const [newFilters, setNewFilters] = useState(selectedFilters);
  const [activeFilterKey, setActiveFilterKey] = useState(null);

  const newFilterLength = useMemo(() => {
    return newFilters ? Object.values(newFilters).flat().length : 0;
  }, [newFilters]);

  const selectedFilterLength = useMemo(() => {
    return selectedFilters ? Object.values(selectedFilters).flat().length : 0;
  }, [selectedFilters]);

  useEffect(() => {
    setNewFilters(selectedFilters);
  }, [selectedFilters, open]);

  const handleClick = (event, key = null) => {
    setAnchorEl(event.currentTarget);
    setActiveFilterKey(key);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setActiveFilterKey(null);
  };

  const onApplyFilters = () => {
    setSelectedFilters(newFilters);
    setActiveFilterKey(null);
    setAnchorEl(null);
  };

  const shouldDisableApply = () => {
    if (selectedFilterLength > 0) {
      return false;
    }

    return newFilterLength < 1;
  };

  const handleReset = () => {
    setNewFilters({});
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <ActiveButtonBadge
        variant="dot"
        color="secondary"
        overlap="circular"
        invisible={!selectedFilterLength}
      >
        <QuickFilterBox onClick={handleClick}>
          <FilterIconSvg
            sx={{ color: selectedFilterLength > 0 ? "#2E2E2E" : "#686868" }}
          />
          <Box sx={{ display: "flex" }}>
            <Typography
              variant="fw400s12c3D4B5A"
              sx={{
                lineHeight: "16px",
                fontWeight: 500,
                color: selectedFilterLength > 0 ? "#2E2E2E" : "#3D4B5A",
              }}
            >
              Filters
            </Typography>
            {selectedFilterLength > 0 && (
              <Typography
                variant="fw400s12c3D4B5A"
                sx={{
                  lineHeight: "16px",
                  fontWeight: 500,
                  color: selectedFilterLength > 0 ? "#2E2E2E" : "#3D4B5A",
                }}
              >
                &nbsp;{`(${selectedFilterLength})`}
              </Typography>
            )}
          </Box>
          <ChevronRight
            className="chevron-icon"
            fontSize="medium"
            sx={{
              color: selectedFilterLength > 0 ? "#15191E" : "#494949",
              transform: "rotate(90deg)",
            }}
          />
        </QuickFilterBox>
      </ActiveButtonBadge>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: styles.popoverPaper } }}
        container={container || undefined}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={styles.header}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FilterIconSvg sx={{ color: "#5E758D" }} />
              <Typography sx={{ mx: 1 }} variant="fw700s14lh24c3D4B5A">
                {" "}
                {t("generic_table:filters.title")}{" "}
              </Typography>
              <RecordCountBox
                fontVariant={"fw600s10lh24c6D72F6"}
                count={newFilterLength}
              />
            </Box>

            {newFilterLength > 0 && (
              <PrimaryTextButton onClick={handleReset} sx={{ fontWeight: 500 }}>
                {" "}
                {t("generic_table:filters.reset")}
              </PrimaryTextButton>
            )}
          </Box>

          <FilterBody
            filters={filters}
            newFilters={newFilters}
            setNewFilters={setNewFilters}
            activeFilterKey={activeFilterKey}
          />
          <Box sx={styles.footer}>
            <CancelFormActionButton onClick={handleClose}>
              {" "}
              {t("actions.cancel")}{" "}
            </CancelFormActionButton>
            <PrimaryFormActionButton
              onClick={onApplyFilters}
              disabled={shouldDisableApply()}
            >
              {t("actions.apply")}
            </PrimaryFormActionButton>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
